import React, { useMemo } from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceDot, Label } from 'recharts';

const FundingFlowChart = ({ phasingData }) => {
  // Helper to safely convert values
  const safeValue = (val) => {
    const result = val / 1000000;
    return (isFinite(result) && result >= 0) ? result : 0;
  };

  // Build chart data from actual phasing table with validation
  const chartData = useMemo(() => {
    return phasingData.map((row) => ({
      year: `Year ${row.year}`,
      yearNum: row.year,
      outlay: safeValue(row.annualDevelopment || 0), // Annual DEVEX spend
      recycled: safeValue(row.recycledDevex || 0), // Funds returned
      netInPlay: safeValue(row.cumulativeNetFunding || 0) // Cumulative net funding
    }));
  }, [phasingData]);

  // Calculate dynamic Y-axis max with robust validation
  const yAxisMax = useMemo(() => {
    const allValues = chartData.flatMap(d => [d.outlay, d.recycled, d.netInPlay]);
    const validValues = allValues.filter(v => isFinite(v) && v >= 0);
    
    if (validValues.length === 0) {
      return 50; // Default max of 50m if no valid data
    }
    
    const maxValue = Math.max(...validValues);
    
    // Ensure we have a reasonable max
    if (maxValue <= 0) {
      return 50;
    }
    
    // Add 15% padding and round up to nearest 5
    const paddedMax = maxValue * 1.15;
    return Math.ceil(paddedMax / 5) * 5;
  }, [chartData]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-white p-4 border-2 rounded-lg shadow-xl" style={{ borderColor: '#48A46A' }}>
          <p className="font-bold mb-3 text-base" style={{ color: '#0A1738' }}>
            Year {data.yearNum}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#48A46A', opacity: 0.6 }}></div>
              <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                GBE Outlay: <span className="font-bold">£{data.outlay.toFixed(2)}m</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#B9E4C9', opacity: 0.5 }}></div>
              <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                Funds Recycled: <span className="font-bold">£{data.recycled.toFixed(2)}m</span>
              </span>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t-2" style={{ borderColor: '#e0e0e0' }}>
              <div className="w-4 h-1 rounded" style={{ backgroundColor: '#0A1738' }}></div>
              <span className="text-sm font-bold" style={{ color: '#0A1738' }}>
                Net in Play: £{data.netInPlay.toFixed(2)}m
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={380}>
        <ComposedChart data={chartData} margin={{ top: 20, right: 80, left: 20, bottom: 10 }}>
          <defs>
            {/* CPS green for Outlay at 60% opacity */}
            <linearGradient id="outlayGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#48A46A" stopOpacity={0.6}/>
              <stop offset="100%" stopColor="#48A46A" stopOpacity={0.2}/>
            </linearGradient>
            {/* Pale green for Recycled at 50% opacity */}
            <linearGradient id="recycledGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B9E4C9" stopOpacity={0.5}/>
              <stop offset="100%" stopColor="#B9E4C9" stopOpacity={0.15}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          
          <XAxis 
            dataKey="year" 
            tick={{ fill: '#2C3E50', fontSize: 12, fontFamily: 'Lato, sans-serif' }}
            angle={-45}
            textAnchor="end"
            height={80}
            stroke="#d1d5db"
          />
          
          <YAxis 
            domain={[0, yAxisMax]}
            tickFormatter={(value) => {
              // Force clean number formatting
              if (!isFinite(value)) return '0';
              return Math.round(value).toString();
            }}
            tick={{ fill: '#2C3E50', fontSize: 12, fontFamily: 'Lato, sans-serif' }}
            label={{ 
              value: 'Funding (£m)', 
              angle: -90, 
              position: 'insideLeft', 
              fill: '#2C3E50', 
              fontFamily: 'Lato, sans-serif',
              style: { textAnchor: 'middle' }
            }}
            stroke="#d1d5db"
            allowDataOverflow={false}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Legend 
            verticalAlign="top" 
            height={45}
            iconType="square"
            wrapperStyle={{ 
              fontFamily: 'Lato, sans-serif', 
              fontSize: '13px',
              paddingBottom: '15px'
            }}
          />
          
          {/* GBE Outlay - solid green filled area with smooth curve */}
          <Area 
            type="basis" 
            dataKey="outlay" 
            stroke="#48A46A" 
            strokeWidth={2}
            fill="url(#outlayGradient)"
            name="GBE Outlay"
            animationDuration={1200}
            animationEasing="ease-in-out"
          />
          
          {/* Funds Recycled - pale green filled area with smooth curve */}
          <Area 
            type="basis" 
            dataKey="recycled" 
            stroke="#B9E4C9" 
            strokeWidth={2}
            fill="url(#recycledGradient)"
            name="Funds Recycled"
            animationDuration={1200}
            animationEasing="ease-in-out"
          />
          
          {/* Net Funds in Play - dark navy smooth line (the funding heartbeat) */}
          <Line 
            type="basis" 
            dataKey="netInPlay" 
            stroke="#0A1738" 
            strokeWidth={3}
            dot={false}
            name="Net Funds in Play"
            animationDuration={1400}
            animationEasing="ease-in-out"
          />
          
          {/* Annotation label */}
          <text
            x="95%"
            y="50%"
            textAnchor="end"
            style={{
              fontSize: '11px',
              fontFamily: 'Lato, sans-serif',
              fill: '#48A46A',
              fontStyle: 'italic'
            }}
          >
            <tspan x="95%" dy="0">Funds recycled</tspan>
            <tspan x="95%" dy="14">to enable next</tspan>
            <tspan x="95%" dy="14">wave of projects</tspan>
          </text>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FundingFlowChart;
